'use client'

import { useEffect, useState } from 'react'
import Pusher from 'pusher-js'

export default function LiveRepliesBanner({ 
  threadId, 
  currentUserId 
}: { 
  threadId: string, 
  currentUserId: string | null 
}) {
  const [newRepliesCount, setNewRepliesCount] = useState(0)
  const [typingUsers, setTypingUsers] = useState<string[]>([])

  useEffect(() => {
    let pusherChannel: any = null
    let pusherClient: any = null

    try {
      const pusherKey = process.env.NEXT_PUBLIC_PUSHER_KEY
      if (pusherKey) {
        pusherClient = new Pusher(pusherKey, { cluster: 'eu' })
        pusherChannel = pusherClient.subscribe(`thread-${threadId}`)
        
        pusherChannel.bind('new-reply', (data: any) => {
          if (data.authorId === currentUserId) return 
          setNewRepliesCount((prev) => prev + 1)
        })
      }
    } catch (pusherErr) {
      console.warn("Pusher setup skipped:", pusherErr)
    }

    console.log("Connecting to SSE stream for thread:", threadId)
    const eventSource = new EventSource(`/api/threads/${threadId}/stream`)

    eventSource.onopen = () => {
      console.log("🟢 SSE Stream Connected Successfully!")
    }

    eventSource.onerror = (err) => {
      console.error("🔴 SSE Stream Error:", err)
    }

    // CRITICAL: Must match the named event 'typing' sent by sseManager
    eventSource.addEventListener('typing', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data)
        console.log("⌨️ RECEIVED TYPING EVENT FROM SERVER:", data)

        if (data.userId !== currentUserId) {
          setTypingUsers(prev => {
            if (data.isTyping && !prev.includes(data.username)) {
              return [...prev, data.username]
            }
            if (!data.isTyping) {
              return prev.filter(u => u !== data.username)
            }
            return prev
          })
        }
      } catch (parseErr) {
        console.error("Failed to parse typing payload:", parseErr)
      }
    })

    return () => {
      if (pusherClient && pusherChannel) {
        pusherClient.unsubscribe(`thread-${threadId}`)
      }
      eventSource.close()
    }
  }, [threadId, currentUserId])

  if (newRepliesCount === 0 && typingUsers.length === 0) {
    return null
  }

  return (
    <>
      {typingUsers.length > 0 && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-40 bg-slate-950/90 border border-indigo-500/35 backdrop-blur-md px-4 py-1.5 rounded-sm shadow-xl text-[11px] font-mono text-indigo-300 flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" />
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]" />
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.4s]" />
          </div>
          <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing code...</span>
        </div>
      )}

      {newRepliesCount > 0 && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50">
          <button 
            type="button"
            onClick={() => window.location.reload()} 
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-sm shadow-lg font-mono font-bold text-xs flex items-center gap-2 transition-all animate-bounce border border-indigo-400/40"
          >
            <span>{newRepliesCount} new {newRepliesCount === 1 ? 'reply' : 'replies'} available</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      )}
    </>
  )
}